import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import {
    Search, X, ChevronRight, LayoutDashboard, Inbox, Users, FileText, Send,
    BarChart2, Settings, CreditCard, Puzzle, MessageSquare, Zap, Building2,
    ShoppingCart, Database, Layout, ClipboardList, Instagram, Calendar,
    Megaphone, Link2, Bell, ShoppingBag, PieChart, UserPlus, Layers,
    HelpCircle, BookOpen, Contact2, Clock, MousePointer2,
    MessageCircle, Bot
} from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { LIVE_CHAT_WORKSPACE_PATH } from '@/lib/constants/aiAgentsRoutes';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export interface SearchResult {
    id: string;
    title: string;
    description: string;
    path: string;
    icon: React.ElementType;
    keywords: string[];
    isComingSoon?: boolean;
    featureKey?: string | null;
    allowedRoles?: string[];
}

/**
 * Single place that decides HOW to open a search result — internal SPA route
 * vs. external link (the WhatsApp support chat). Every surface that renders
 * SEARCH_ITEMS (the Ctrl+K dialog AND TopNavigation's inline/mobile
 * suggestions) must go through this instead of calling `navigate(item.path)`
 * directly, or an external `path` gets treated as a relative route (e.g.
 * `/home/https://wa.me/...` → 404) the moment a second surface is added.
 */
export const openSearchResult = (item: SearchResult, navigate: NavigateFunction) => {
    if (item.path.startsWith('http')) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
        navigate(item.path);
    }
};

export const SEARCH_ITEMS: SearchResult[] = [
    // Standalone / Main
    {
        id: 'overview',
        title: 'Overview',
        description: 'Global overview of your account',
        path: '/home',
        icon: LayoutDashboard,
        keywords: ['home', 'dashboard', 'stats'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'inbox',
        title: 'All Messages',
        description: 'All your customer messages in one place',
        path: '/home/inbox',
        icon: Inbox,
        keywords: ['chat', 'messages', 'whatsapp', 'instagram'],
        featureKey: 'inbox',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    // Campaigns Group — point at the new redesigned Campaigns/Templates
    // pages (same routes the sidebar now links to post-cutover), not the
    // hidden old /home/campaigns + /home/templates pages.
    {
        id: 'templates',
        title: 'Message Templates',
        description: 'Manage and create WhatsApp message templates',
        path: '/home/new-templates',
        icon: FileText,
        keywords: ['whatsapp', 'templates', 'hsm'],
        featureKey: 'templates',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'campaigns',
        title: 'WhatsApp Campaigns',
        description: 'Launch bulk messaging campaigns',
        path: '/home/new-campaigns',
        icon: Send,
        keywords: ['broadcast', 'marketing', 'bulk'],
        featureKey: 'campaigns',
        allowedRoles: ['ADMIN', 'USER'],
    },
    // {
    //     id: 'instagram-broadcasts',
    //     title: 'Instagram Broadcasts',
    //     description: 'Send broadcast messages on Instagram',
    //     path: '/home/instagram-broadcasts',
    //     icon: Instagram,
    //     keywords: ['ig', 'broadcast', 'marketing'],
    //     featureKey: 'instagram_broadcasts',
    //     allowedRoles: ['ADMIN'],
    // },
    {
        id: 'social-media-posts',
        title: 'Social Media Posts',
        description: 'Create, schedule, and manage Instagram, Facebook, and LinkedIn posts',
        path: '/home/social-media/posts',
        icon: Layers,
        keywords: ['social', 'posts', 'instagram', 'facebook', 'linkedin', 'all', 'schedule', 'scheduling', 'calendar'],
        allowedRoles: ['ADMIN'],
    },

    {
        id: 'instagram-posts',
        title: 'Instagram Posts',
        description: 'Manage and schedule your Instagram posts',
        // Instagram posts live as a tab of the Social Media Posts workspace; the
        // old `/home/instagram/posts` path only redirects here now.
        path: '/home/social-media/posts?platform=instagram',
        icon: Instagram,
        keywords: ['social', 'posts', 'instagram', 'feed'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'facebook-posts',
        title: 'Facebook Posts',
        description: 'Manage and schedule your Facebook posts',
        // Facebook posts live as a tab of the Social Media Posts workspace; the
        // old `/home/facebook/posts` path only redirects here now.
        path: '/home/social-media/posts?platform=facebook',
        icon: MessageCircle,
        keywords: ['social', 'posts', 'facebook', 'feed'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'linkedin-posts',
        title: 'LinkedIn Posts',
        description: 'Manage and schedule your LinkedIn posts',
        // LinkedIn posts live as a tab of the Social Media Posts workspace; the
        // old `/home/linkedin/posts` path only redirects here now.
        path: '/home/social-media/posts?platform=linkedin',
        icon: Puzzle,
        keywords: ['social', 'posts', 'linkedin', 'professional'],
        allowedRoles: ['ADMIN'],
    },

    // Customers Group
    {
        id: 'all-contacts',
        title: 'Contacts',
        description: 'Manage your primary contact database',
        // Same destination the sidebar's "CRM" entry links to — searching
        // "Contacts" should land on CRM, not the old flat /home/contacts list.
        path: '/home/boards/leads',
        icon: Users,
        keywords: ['people', 'contacts', 'crm'],
        featureKey: 'contacts',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'customers',
        title: 'Customers',
        description: 'Manage your CRM customers',
        path: '/home/customers',
        icon: Users,
        keywords: ['customers', 'crm', 'clients'],
        featureKey: 'contacts',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'leads',
        title: 'Leads',
        description: 'Track and manage your sales leads',
        path: '/home/leads',
        icon: UserPlus,
        keywords: ['prospects', 'sales', 'leads'],
        featureKey: 'leads',
        allowedRoles: ['ADMIN'],
    },
    // 'Segments' and 'Chat History' removed from search (2026-08-28) — both
    // pages (pages/crm/Segments.tsx, pages/crm/ChatHistory.tsx) are 8-line
    // `<ComingSoon />` stubs with zero other in-app entry points (already
    // fully removed from the sidebar). Unlike the other fixes on this list,
    // there is no "new design" page to redirect to — the features were never
    // built, so surfacing them as search results is misleading rather than a
    // stale route. Re-add once a real destination exists.
    {
        id: 'notes-and-tags',
        title: 'Notes and Tags',
        description: 'Organize contacts with custom labels',
        path: '/home/notes-and-tags',
        icon: ClipboardList,
        keywords: ['labels', 'notes', 'tags'],
        featureKey: 'notes_and_tags',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'boards',
        title: 'Customer Boards',
        description: 'Manage pipelines and tasks',
        path: '/home/boards',
        icon: Layout,
        keywords: ['kanban', 'pipeline', 'tasks'],
        featureKey: 'contacts',
        allowedRoles: ['ADMIN', 'USER'],
    },

    // Automation Group
    {
        id: 'whatsapp-automation',
        title: 'Automations',
        description: 'Automate WhatsApp, Instagram, and Facebook workflows',
        path: '/home/automation',
        icon: Zap,
        keywords: ['auto', 'bot', 'replies', 'automation', 'whatsapp', 'instagram', 'facebook'],
        featureKey: 'automations',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'whatsapp-flows',
        title: 'Forms',
        description: 'Create interactive shopping and booking flows',
        path: '/home/flows',
        icon: Layout,
        keywords: ['flows', 'forms', 'interactive', 'whatsapp forms'],
        featureKey: 'whatsapp_flows',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'form-data',
        title: 'Form Submissions',
        description: 'View data from WhatsApp forms',
        // Points at the Forms workspace tab, NOT the standalone `/home/form-data`
        // page, whose sidebar entry was hidden on 2026-08-03. Same screen either
        // way, but this one is a visible destination — landing on the hidden
        // route would leave no sidebar item highlighted.
        path: '/home/flows?view=submissions',
        icon: Database,
        keywords: ['data', 'export', 'responses', 'forms'],
        featureKey: 'whatsapp_flows',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'instagram-automation',
        title: 'Instagram Automations',
        description: 'Automate Instagram interactions',
        path: '/home/automation/instagram',
        icon: Instagram,
        keywords: ['ig', 'bot', 'auto', 'automation'],
        featureKey: 'instagram_automation',
        allowedRoles: ['ADMIN'],
    },

    // Ads Manager Group
    {
        id: 'meta-ads',
        title: 'Meta Ads',
        description: 'Manage your Meta advertising campaigns',
        path: '/home/ads',
        icon: Megaphone,
        keywords: ['facebook', 'instagram', 'ads'],
        featureKey: 'meta_ads',
        allowedRoles: ['ADMIN'],
    },

    // Schedulers Group
    {
        id: 'scheduling-overview',
        title: 'Scheduling Overview',
        description: 'Overview of your appointments and slots',
        path: '/home/scheduling-overview',
        icon: Clock,
        keywords: ['appointments', 'slots', 'overview'],
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'meeting-links',
        title: 'Meetings',
        description: 'Create and manage meeting invitation links',
        path: '/home/meeting-links',
        icon: Link2,
        keywords: ['links', 'meetings', 'invite'],
        featureKey: 'meeting_links',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'time-slots',
        title: 'Availability Slots',
        description: 'Manage available booking slots',
        path: '/home/time-slots',
        icon: MousePointer2,
        keywords: ['booking', 'slots', 'available'],
        featureKey: 'time_slots',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'scheduling-integrations',
        title: 'Calendar Integrations',
        description: 'Connect with Calendar apps',
        path: '/home/scheduling-integrations',
        icon: Puzzle,
        keywords: ['booking', 'sync', 'calendar'],
        featureKey: 'integrations',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'scheduling',
        title: 'Scheduling',
        description: 'Manage your booking types and schedules',
        path: '/home/scheduling',
        icon: Calendar,
        keywords: ['booking', 'schedule', 'appointments'],
        featureKey: 'scheduling',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'whatsapp-reminders',
        title: 'WhatsApp Reminders',
        description: 'Send automated reminders on WhatsApp',
        path: '/home/whatsapp-reminders',
        icon: Bell,
        keywords: ['reminders', 'alerts', 'notifications'],
        featureKey: 'whatsapp_reminders',
        allowedRoles: ['ADMIN'],
    },

    // Online Store Group
    {
        id: 'products',
        title: 'Product Catalog',
        description: 'Manage your product catalog',
        // The sidebar's Commerce group points here, not the old standalone
        // `/home/products` page (`Products.tsx`, unmaintained since restructure).
        path: '/home/commerce/products',
        icon: ShoppingCart,
        keywords: ['catalog', 'items', 'store'],
        featureKey: 'products',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'orders',
        title: 'Orders',
        description: 'Manage your store orders',
        path: '/home/orders',
        icon: ShoppingBag,
        keywords: ['sales', 'orders', 'ecommerce'],
        featureKey: 'orders',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'abonded-carts',
        title: 'Abandoned Carts',
        description: 'Recover abandoned shopping carts',
        path: '/home/abonded-carts',
        icon: ShoppingBag,
        keywords: ['recovery', 'carts', 'sales'],
        featureKey: 'abonded_carts',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'payments',
        title: 'Payments',
        description: 'Manage transactions and payouts',
        path: '/home/payments',
        icon: CreditCard,
        keywords: ['money', 'transactions', 'payouts'],
        featureKey: 'payments',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'store-reports',
        title: 'Commerce Reports',
        description: 'View ecommerce performance reports',
        path: '/home/store-reports',
        icon: BarChart2,
        keywords: ['analytics', 'reports', 'ecommerce'],
        featureKey: 'store_reports',
        allowedRoles: ['ADMIN'],
    },

    // Analytics Group
    {
        id: 'analytics',
        title: 'Analytics',
        description: 'Main reporting dashboard',
        path: '/home/analytics',
        icon: PieChart,
        keywords: ['reports', 'stats', 'data', 'campaign', 'performance'],
        featureKey: 'analytics',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'message-reports',
        title: 'Message Reports',
        description: 'Detailed analysis of message delivery',
        path: '/home/message-reports',
        icon: FileText,
        keywords: ['delivery', 'reports', 'stats'],
        featureKey: 'message_reports',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'ads-results',
        title: 'Ads Results',
        description: 'View results from your Meta ads',
        // The old standalone `/home/ads-results` page is a "Coming Soon"
        // placeholder now — the real ad reporting lives in the Ads workspace.
        path: '/home/ads',
        icon: Megaphone,
        keywords: ['roi', 'ads', 'results'],
        featureKey: 'meta_ads',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'sales-and-conversions',
        title: 'Sales and Conversions',
        description: 'Track your shop sales and conversions',
        path: '/home/sales-and-conversions',
        icon: ShoppingBag,
        keywords: ['conversions', 'sales', 'revenue'],
        featureKey: 'sales_and_conversions',
        allowedRoles: ['ADMIN'],
    },

    // Account & Setup Group
    {
        id: 'settings',
        title: 'Settings',
        description: 'Manage your account settings',
        // The old `/home/settings` page (Settings.tsx) has been superseded by
        // the unified Account Settings shell — same page the sidebar's
        // "General Settings" row and the header avatar menu's Settings item
        // point to.
        path: '/home/account-settings',
        icon: Settings,
        keywords: ['account', 'profile', 'config'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'team-management',
        title: 'Team Management',
        description: 'Manage your team members',
        path: '/home/team-management',
        icon: Users,
        keywords: ['team', 'members', 'permissions'],
        featureKey: 'team_management',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'integrations',
        title: 'Integrations',
        description: 'Connect with third-party apps',
        path: '/home/integrations',
        icon: Puzzle,
        keywords: ['apps', 'api', 'connect'],
        featureKey: 'integrations',
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'billing',
        title: 'Billing',
        description: 'Manage your subscription and invoices',
        // The bare `/home/billing` page renders the same content with no
        // settings-shell chrome around it — land inside the unified Account
        // Settings shell instead, same as the sidebar's General Settings entry.
        path: '/home/account-settings?section=billing',
        icon: CreditCard,
        keywords: ['payment', 'subscription', 'plan'],
        allowedRoles: ['ADMIN', 'USER'],
    },
    {
        id: 'business-accounts',
        title: 'Business Accounts',
        description: 'Manage WhatsApp Business Accounts',
        // Same content, wrapped in the unified Account Settings shell —
        // matching the Billing fix above.
        path: '/home/account-settings?section=business-accounts',
        icon: Building2,
        keywords: ['whatsapp', 'waba', 'accounts'],
        featureKey: 'business_accounts',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'commerce-settings',
        title: 'Commerce Settings',
        description: 'Manage Meta commerce settings',
        // Land directly on the Settings tab of the Commerce page, not its
        // default Products tab.
        path: '/home/commerce/products?tab=settings',
        icon: ShoppingCart,
        keywords: ['shop', 'meta', 'commerce'],
        featureKey: 'commerce',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'social-accounts',
        title: 'Social Accounts',
        description: 'Connect your social media profiles',
        // Lives under Integrations now (/home/social-accounts still redirects
        // here; pointing straight at the tab avoids the extra hop).
        path: '/home/integrations?tab=social',
        icon: Instagram,
        keywords: ['connect', 'social', 'profiles'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'ai-agents',
        title: 'AI Agents',
        description: 'Deploy AI assistants to handle queries',
        path: '/home/ai-agents',
        icon: Bot,
        keywords: ['ai', 'bot', 'assistant'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'live-chat',
        title: 'Live Chat',
        description: 'Real-time customer support channels',
        // Live Chat moved into the unified AI Agents workspace; the sidebar
        // points here too (see lib/constants/aiAgentsRoutes.ts). The old
        // `/home/live-chat` channels list is still routed but is a legacy
        // surface, not the destination search should send people to.
        path: LIVE_CHAT_WORKSPACE_PATH,
        icon: MessageSquare,
        keywords: ['support', 'chat', 'live'],
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'notifications',
        title: 'Notifications',
        description: 'View your recent activity and alerts',
        path: '/home/notifications',
        icon: Bell,
        keywords: ['alerts', 'updates', 'unread'],
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },

    // Support Group
    {
        id: 'whats-new',
        title: 'Updates',
        description: 'Release notes and product updates',
        path: '/home/updates',
        icon: Megaphone,
        keywords: ['release', 'notes', 'changelog', 'updates', 'new'],
        featureKey: 'whats_new',
        allowedRoles: ['ADMIN', 'USER', 'AGENT'],
    },
    {
        id: 'how-to-use',
        title: 'How to Use',
        description: 'Getting started guides and tutorials',
        path: '/home/how-to-use',
        icon: HelpCircle,
        keywords: ['guides', 'tutorials', 'start'],
        featureKey: 'how_to_use',
        allowedRoles: ['ADMIN'],
    },
    {
        id: 'help-articles',
        title: 'Help Articles',
        description: 'Browse our help documentation',
        path: '/home/help-articles',
        icon: BookOpen,
        keywords: ['documentation', 'articles', 'help'],
        featureKey: 'help_articles',
        allowedRoles: ['ADMIN'],
    },
    // 'Raise Ticket' removed from search (2026-08-28) — same class as Segments
    // and Chat History above: pages/help/RaiseTickets.tsx is an 8-line
    // `<ComingSoon />` stub, and its two sidebar entries are already commented
    // out. No real destination to send people to.
    {
        id: 'contact-support',
        title: 'Contact Support',
        description: 'Chat with our support team on WhatsApp',
        // The old standalone `/home/contact-support` page is unmaintained.
        // Same destination the sidebar's own "Contact Support" row and the
        // How to Use page's CTA button use (CONTACT_SUPPORT_WHATSAPP_URL in
        // pages/help/HowToUse.tsx — kept as a literal here, not imported, so
        // this eagerly-bundled search module doesn't pull in that whole page).
        path: 'https://wa.me/918977232350',
        icon: Contact2,
        keywords: ['chat', 'call', 'support', 'whatsapp'],
        featureKey: 'contact_support',
        allowedRoles: ['ADMIN'],
    },
];

/**
 * Role + feature filtering shared by every surface that renders SEARCH_ITEMS
 * (the Ctrl+K dialog AND the TopNavigation inline suggestions). Items the
 * current user's role or the org's plan doesn't allow are hidden so search
 * never routes users into a FeatureRoute/RoleRoute redirect loop.
 */
export const useAvailableSearchItems = (): SearchResult[] => {
    const { user } = useAuth();
    const { data: entitlements } = useOrganizationEntitlements();

    const roleRaw = (user?.role || '').toString().toUpperCase();
    const isSuperAdmin = !!(user as any)?.is_super_admin;
    const isOwner = !!(user as any)?.is_organization_owner;
    const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
    const currentRole = isAdmin ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : 'USER');

    const features = entitlements?.entitlements?.features;

    return React.useMemo(() => {
        const enabledFeatures = features || {};
        return SEARCH_ITEMS.filter(item => {
            // Role check
            if (item.allowedRoles && !item.allowedRoles.includes(currentRole)) {
                return false;
            }

            // Feature check
            if (item.featureKey) {
                // Some features are always enabled for search if they exist in sidebar
                const alwaysSearchable = ['integrations', 'billing', 'whats_new', 'how_to_use', 'help_articles', 'raise_ticket', 'contact_support'];
                if (alwaysSearchable.includes(item.featureKey)) return true;

                return enabledFeatures[item.featureKey] === true;
            }

            return true;
        });
    }, [currentRole, features]);
};

interface GlobalSearchProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onOpenChange }) => {
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    const availableItems = useAvailableSearchItems();

    const [results, setResults] = useState<SearchResult[]>(availableItems);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setQuery('');
            setResults(availableItems);
            setSelectedIndex(0);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open, availableItems]);

    useEffect(() => {
        if (!query) {
            setResults(availableItems);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const filtered = availableItems.filter((item) => {
            return (
                item.title.toLowerCase().includes(lowerQuery) ||
                item.description.toLowerCase().includes(lowerQuery) ||
                item.keywords.some((k) => k.toLowerCase().includes(lowerQuery))
            );
        });
        setResults(filtered);
        setSelectedIndex(0);
    }, [query, availableItems]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[selectedIndex]) {
                handleSelect(results[selectedIndex]);
            }
        } else if (e.key === 'Escape') {
            onOpenChange(false);
        }
    };

    const handleSelect = (item: SearchResult) => {
        if (item.isComingSoon) return;
        openSearchResult(item, navigate);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="p-0 gap-0 max-w-2xl bg-background/95 backdrop-blur-xl border-border shadow-2xl overflow-hidden [&>button]:hidden duration-200">
                <DialogTitle className="sr-only">Global Search</DialogTitle>
                <div className="flex flex-col h-[60vh] md:h-[600px]">
                    {/* Header / Search Input */}
                    <div className="flex items-center px-4 py-4 border-b border-border bg-muted/20">
                        <Search className="w-5 h-5 text-muted-foreground mr-3" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search for pages..."
                            className="flex-1 bg-transparent border-none outline-none text-lg placeholder:text-muted-foreground/50 text-foreground"
                        />
                        <button
                            onClick={() => onOpenChange(false)}
                            className="p-1 hover:bg-muted rounded-md transition-colors"
                        >
                            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                                <span className="text-xs">ESC</span>
                            </kbd>
                        </button>
                    </div>

                    {/* Results List */}
                    <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
                        {results.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                <Search className="w-12 h-12 mb-4 opacity-20" />
                                <p>No results found for "{query}"</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {results.map((item, index) => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleSelect(item)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                        className={cn(
                                            "flex items-center px-4 py-3 rounded-lg cursor-pointer transition-all duration-200 group relative",
                                            selectedIndex === index
                                                ? "bg-primary/10"
                                                : "hover:bg-muted/50",
                                            item.isComingSoon && "opacity-60 cursor-not-allowed"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-md mr-4 transition-colors",
                                            selectedIndex === index ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                        )}>
                                            <item.icon className="w-5 h-5" />
                                        </div>

                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className={cn(
                                                    "font-medium transition-colors",
                                                    selectedIndex === index ? "text-primary" : "text-foreground"
                                                )}>
                                                    {item.title}
                                                </h4>
                                                {item.isComingSoon && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold uppercase tracking-wider">
                                                        Soon
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                                                {item.description}
                                            </p>
                                        </div>

                                        {!item.isComingSoon && (
                                            <ChevronRight className={cn(
                                                "w-5 h-5 transition-all duration-200",
                                                selectedIndex === index
                                                    ? "opacity-100 text-primary translate-x-0"
                                                    : "opacity-0 -translate-x-2"
                                            )} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between text-[10px] text-muted-foreground">
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                                <kbd className="rounded border px-1 bg-background">↑↓</kbd> to navigate
                            </span>
                            <span className="flex items-center gap-1">
                                <kbd className="rounded border px-1 bg-background">Enter</kbd> to select
                            </span>
                        </div>
                        <div>
                            {results.length} result{results.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
